// src/components/QRLoginScanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { QrReader } from 'react-qr-reader';
import { FaArrowLeft, FaCameraRotate, FaExclamation } from 'react-icons/fa6';
import translations from './translations.json';
import './css/qrLoginScanner.css';

const QRLoginScanner = ({ onClose }) => {
  // Translation setup
  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'nl';
  const t = translations[preferredLanguage] || translations['nl'];

  const [scannedResult, setScannedResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [key, setKey] = useState(0);
  const [isCameraActive, setIsCameraActive] = useState(true);

  // Rate limiting
  const lastScanTimeRef = useRef(0);
  const scanIntervalMs = 500;

  // Function to validate JWT format
  const isValidJWT = (token) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      // Try to decode the header and payload (basic validation)
      const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Check if it has typical JWT structure
      return header && payload && (header.typ === 'JWT' || header.alg);
    } catch (error) {
      return false;
    }
  };

  // Function to extract username from JWT
  const extractUsernameFromJWT = (token) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Try different common username fields in JWT payload
      return payload.username || payload.sub || payload.user || payload.email || null;
    } catch (error) {
      return null;
    }
  };

  // Process QR code when scanned result changes
  useEffect(() => {
    if (scannedResult && isCameraActive) {
      processQRCode(scannedResult);
    }
  }, [scannedResult, isCameraActive]);

  const processQRCode = async (result) => {
    // Prevent processing if already loading
    if (loading) return;

    setLoading(true);
    setErrorMessage('');

    try {
      // Specifically validate for JWT tokens only
      if (!isValidJWT(result)) {
        setErrorMessage(t.invalidQRCode);
        setLoading(false);
        setTimeout(resetScanner, 2000);
        return;
      }

      // Extract username from JWT
      const username = extractUsernameFromJWT(result);
      if (!username) {
        setErrorMessage(t.noUsernameFound);
        setLoading(false);
        setTimeout(resetScanner, 2000);
        return;
      }

      // Store the JWT as both access and refresh token (same as normal login)
      localStorage.setItem('accessToken', result);
      localStorage.setItem('loginSuccessful', 'true');
      localStorage.setItem('username', username);
      sessionStorage.setItem('freshLogin', 'true');

      // Show 10% chance testimonial popup (same as normal login)
      if (Math.random() < 0.1) {
        window.open('https://testimonial.to/mateza-booking', '_blank');
      }

      // Reload the page to trigger app state change
      window.location.reload();
      
    } catch (error) {
      console.error('QR login failed:', error);
      setErrorMessage(t.qrLoginFailed);
      setTimeout(resetScanner, 2000);
    } finally {
      setLoading(false);
    }
  };

  // Toggle camera function
  const toggleCamera = () => {
    stopCamera();
    setFacingMode(prevMode => prevMode === 'environment' ? 'user' : 'environment');
    setKey(prevKey => prevKey + 1);
    setTimeout(() => {
      startCamera();
    }, 300);
  };

  // Camera control functions
  const stopCamera = () => {
    setIsCameraActive(false);
  };

  const startCamera = () => {
    setIsCameraActive(true);
  };

  // Cleanup function
  const cleanup = () => {
    stopCamera();
    setScannedResult(null);
    setErrorMessage('');
    setLoading(false);
    lastScanTimeRef.current = 0;
  };

  // Handle scan results
  const handleScan = (result, error) => {
    const now = Date.now();
    
    if (result && 
        now - lastScanTimeRef.current >= scanIntervalMs && 
        isCameraActive && 
        !loading) {
      
      lastScanTimeRef.current = now;
      
      // Only accept potential JWT tokens (longer than 100 chars and contains dots)
      if (result.text && result.text.length > 100 && result.text.includes('.')) {
        setScannedResult(result.text);
      } else {
        console.log('Ignoring non-JWT format:', result.text ? result.text.substring(0, 50) : '');
      }
    }
    
    if (error) {
      console.error('QR scan error:', error);
    }
  };

  // Reset scanner state
  const resetScanner = () => {
    setScannedResult(null);
    setErrorMessage('');
    setLoading(false);
    lastScanTimeRef.current = Date.now() + 500;
  };

  // Handle back button
  const handleBack = () => {
    cleanup();
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  return (
	<div className='qr-page'>
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-container">
        {/* Header with back button */}
        <div className="qr-scanner-header">
          <button className="qr-back-button" onClick={handleBack}>
            <FaArrowLeft size={24} />
          </button>
          <h2 className="qr-scanner-title">{t.qrLoginTitle}</h2>
        </div>

        {loading ? (
          <div className="qr-loading-container">
            <div className="qr-spinner"></div>
            <p>{t.loggingIn}</p>
          </div>
        ) : (
          <>
            {/* Instructions */}
            <div className="qr-scanner-instructions">
              <p>{t.qrInstructions}</p>
            </div>

            {/* Scanner wrapper */}
            <div className="qr-scanner-wrapper">
              {isCameraActive && (
                <QrReader
                  key={`qr-scanner-${key}`}
                  onResult={handleScan}
                  constraints={{ 
                    facingMode: facingMode,
                    video: {
                      facingMode: facingMode,
                      width: { ideal: 1280 },
                      height: { ideal: 720 }
                    }
                  }}
                  containerStyle={{ width: '100%', height: '100%' }}
                  scanDelay={scanIntervalMs}
                  videoStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </div>

            {/* Camera toggle button */}
            <button className="qr-camera-toggle" onClick={toggleCamera}>
              <FaCameraRotate size={30} />
            </button>

            {/* Error message */}
            {errorMessage && (
              <div className="qr-error-message">
                <FaExclamation size={18} />
                <span>{errorMessage}</span>
                <button className="qr-retry-button" onClick={resetScanner}>
                  {t.tryAgain}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
	</div>
  );
};

export default QRLoginScanner;